// Module helper utilities and constants for PMC re-registration

// Step constants
export const STEP_MODULE_SELECTION = 1;
export const STEP_ACCOUNT_DETAILS = 2;
export const STEP_SUPPORTING_DOCUMENTS = 5;

export const DEFAULT_MODULE_DESCRIPTION = "This module involves a mix of formal education and practical experience in programming and software development.";

export const DeliveryMethodMap = {
    "parttimeonline": 0,
    "full": 1,
    "fulltimeonline": 1,
    "fulltimecontact": 1,
    "selfplaced": 1
};

export const yearLabels = {
    "1": "First",
    "2": "Second", 
    "3": "Third",
    "4": "Fourth",
    "First": "First",
    "Second": "Second",
    "Third": "Third",
    "Fourth": "Fourth"
};

export const deriveModuleSource = (module = {}) => {
    if (module.moduleSource) {
        return module.moduleSource;
    }

    const status = (module.status || module.reason || "").toLowerCase();

    if (status === "fail" || status === "failed") {
        return "redo";
    }

    if (status === "eligible") {
        return "progression";
    }

    if (
        status === "credit" ||
        status === "transfer" ||
        status === "enrolled" ||
        status === "not eligible" ||
        status === "noteligible" ||
        status === "not_eligible"
    ) {
        return "transfer";
    }

    return "progression";
};

export const countsTowardsModuleLimit = (source) => source === "redo" || source === "progression";

export const normalizeModuleId = (id) => {
    if (id === null || id === undefined) return null;
    return String(id);
};

export const normalizeModuleName = (name) => {
    return String(name || "").trim().toLowerCase();
};

export const isModuleInList = (moduleId, list) => {
    const normalizedId = normalizeModuleId(moduleId);
    return list.some(id => normalizeModuleId(id) === normalizedId);
};

export const getIdsForName = (name, allModules = []) => {
    const key = normalizeModuleName(name);
    const source = Array.isArray(allModules) ? allModules : Object.values(allModules || {});
    return source
        .filter(m => normalizeModuleName(m.name) === key)
        .map(m => normalizeModuleId(m.netsuite_product_id));
};

export const isModuleSelectedByIdOrName = (moduleId, moduleName, selectedIds, allModules = []) => {
    const normalizedId = normalizeModuleId(moduleId);
    const inById = selectedIds.some(id => normalizeModuleId(id) === normalizedId);
    if (inById) return true;
    const aliases = moduleName ? getIdsForName(moduleName, allModules) : [];
    return aliases.some(aid => selectedIds.some(id => normalizeModuleId(id) === aid));
};

export const getModuleDescription = (module = {}) => {
    // API returns Description (capital D) as HTML
    const description = (module.Description || module.description)?.trim();
    
    if (!description?.length) {
        return DEFAULT_MODULE_DESCRIPTION;
    }
    
    // Strip HTML tags and decode HTML entities
    const textContent = description
        .replace(/<[^>]*>/g, '') // Remove all HTML tags
        .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
        .replace(/&amp;/g, '&') // Replace &amp; with &
        .replace(/&lt;/g, '<') // Replace &lt; with <
        .replace(/&gt;/g, '>') // Replace &gt; with >
        .replace(/&quot;/g, '"') // Replace &quot; with "
        .replace(/&#39;/g, "'") // Replace &#39; with '
        .trim();
    
    // If no actual text content, return default
    return textContent.length > 0 ? textContent : DEFAULT_MODULE_DESCRIPTION;
};

export const isPassedStatus = (status = "") => {
    const normalizedStatus = (status || "").toLowerCase();
    return normalizedStatus === "pass" || normalizedStatus === "passed" || normalizedStatus === "exempt";
};

export const getStatusVariantAndColor = (status) => {
    const statusLower = (status || "").toLowerCase();
    
    if (statusLower === "fail" || statusLower === "failed") {
        return { variant: "error", customColor: "#d9534f" };
    } else if (statusLower === "eligible") {
        return { variant: "info", customColor: "#00acc1" };
    } else if (statusLower === "not eligible" || statusLower === "noteligible" || statusLower === "not_eligible") {
        return { variant: "warning", customColor: "#ffab00" };
    } else if (statusLower === "pass" || statusLower === "passed" || statusLower  === "exempt") {
        return { variant: "success", customColor: "#5cb85c" };
    } else if (statusLower === "enrolled") {
        return { variant: "info", customColor: null };
    } else if (statusLower === "deregister") {
        return { variant: "warning", customColor: "#ffab00" };
    } else if (statusLower === "inactive") {
        return { variant: "info", customColor: "var(--purple)" };
    } else {
        return { variant: "info", customColor: "#6c757d" };
    }
};

/**
 * Convert term to numeric value for sorting (year modules "1, 2" come before semester 1)
 */
const getTermNumeric = (term) => {
    if (term === "1, 2" || term === "1,2") return 0.5; // Year modules sort before semester 1
    const termNum = Number(term);
    return isNaN(termNum) ? 999 : termNum;
};

export const groupModulesByYear = (modulesData) => {
    const grouped = modulesData.reduce((acc, module) => {
        const year = module.year || "First";
        if (!acc[year]) acc[year] = [];
        acc[year].push(module);
        return acc;
    }, {});
    
    // Sort modules within each year group by term (earliest first)
    Object.keys(grouped).forEach(year => {
        grouped[year].sort((a, b) => {
            const termA = getTermNumeric(a.term);
            const termB = getTermNumeric(b.term);
            return termA - termB;
        });
    });
    
    return grouped;
};

export const parseIdList = (value) => {
    if (!value) {
        return [];
    }
    if (Array.isArray(value)) {
        return value;
    }
    return String(value)
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);
};
